import React from "react"
import { cn } from "../utils"
import { cva, VariantProps } from "class-variance-authority"
import { NoteIcon } from "./note-icon"
import { NoteContext } from "./note-context"
import { NoteHeading } from "./note-heading"
import { NoteContent } from "./note-content"
import { NoteDismiss } from "./note-dismiss"
import { NoteActions } from "./note-actions"
import { NoteContentLeading } from "./note-content-leading"
import { restoreFocusBeforeUnmount, type FocusTarget } from "./note-focus"

// =============================================================================
// Styles
// =============================================================================
const sharedStackedLayoutStyles = [
  "grid-cols-[auto_1fr_auto] items-start",
  "[grid-template-areas:'icon_._dismiss'_'content_content_content'_'actions_actions_actions']"
]

const paddedStackedLayoutStyles = [
  "p-4",
  "[&>[data-slot=note-icon]]:mb-4",
  "[&>[data-slot=note-content-leading]]:mb-4",
  "[&>[data-slot=note-dismiss]]:mb-4",
  "[&>[data-slot=note-actions]]:mt-4"
]

const noteStyles = cva(
  [
    "@container/note w-full",
    "rounded-lg ring-1 ring-inset",
    "text-start text-sm leading-6",
    "[&_a:not([data-slot])]:outline-2 [&_a:not([data-slot])]:outline-offset-2 [&_a:not([data-slot])]:outline-transparent [&_a:not([data-slot])]:underline [&_a:not([data-slot])]:rounded-[0.25rem] [&_a:not([data-slot])]:font-medium [&_a:not([data-slot])]:transition-colors [&_a:not([data-slot])]:hover:text-gray-800 [&_a:not([data-slot])]:hover:decoration-gray-800 [&_a:not([data-slot])]:focus-visible:outline-gray-900"
  ],
  {
    variants: {
      variant: {
        info: [
          "ring-warm-white-300 bg-warm-white-100",
          "[&_[data-slot=note-icon]]:text-gray-700"
        ],
        upsell: [
          "ring-blue-200 bg-blue-50",
          "[&_[data-slot=note-icon]]:text-blue-700"
        ],
        success: [
          "ring-green-200 bg-green-50",
          "[&_[data-slot=note-icon]]:text-green-700"
        ],
        warning: [
          "ring-system-yellow-200 bg-system-yellow-50",
          "[&_[data-slot=note-icon]]:text-system-yellow-700"
        ],
        error: [
          "ring-red-200 bg-red-50",
          "[&_[data-slot=note-icon]]:text-red-700"
        ]
      }
    },
    defaultVariants: {
      variant: "info"
    }
  }
)

const noteLayoutStyles = cva("grid", {
  variants: {
    layout: {
      horizontal: [
        ...sharedStackedLayoutStyles,
        ...paddedStackedLayoutStyles,
        "@lg/note:gap-y-4",
        "@lg/note:[grid-template-areas:'icon_content_dismiss']",
        "@lg/note:has-[>[data-slot=note-actions]]:[grid-template-areas:'icon_content_dismiss'_'._actions_.']",
        "@lg/note:[&>[data-slot=note-icon]]:mb-0 @lg/note:[&>[data-slot=note-icon]]:mr-4",
        "@lg/note:[&>[data-slot=note-content-leading]]:mb-0 @lg/note:[&>[data-slot=note-content-leading]]:mr-4",
        "@lg/note:[&>[data-slot=note-dismiss]]:mb-0 @lg/note:[&>[data-slot=note-dismiss]]:ml-4",
        "@lg/note:[&>[data-slot=note-actions]]:mt-0"
      ],
      stacked: [...sharedStackedLayoutStyles, ...paddedStackedLayoutStyles],
      inline: [
        "p-2",
        ...sharedStackedLayoutStyles,
        "[&>[data-slot=note-icon]]:mb-2",
        "[&>[data-slot=note-content-leading]]:mb-2",
        "[&>[data-slot=note-dismiss]]:mb-2",
        "[&>[data-slot=note-actions]]:mt-2",
        "@lg/note:grid-cols-[auto_1fr_auto_auto] @lg/note:items-center",
        "@lg/note:[grid-template-areas:'icon_content_actions_dismiss']",
        "@lg/note:[&>[data-slot=note-icon]]:mb-0 @lg/note:[&>[data-slot=note-icon]]:mr-2",
        "@lg/note:[&>[data-slot=note-content-leading]]:mb-0 @lg/note:[&>[data-slot=note-content-leading]]:mr-2",
        "@lg/note:[&>[data-slot=note-actions]]:mt-0 @lg/note:[&>[data-slot=note-actions]]:ml-2",
        "@lg/note:[&>[data-slot=note-dismiss]]:mb-0 @lg/note:[&>[data-slot=note-dismiss]]:ml-2"
      ]
    }
  },
  defaultVariants: {
    layout: "horizontal"
  }
})

// =============================================================================
// Props
// =============================================================================
export type NoteProps = {
  children: React.ReactNode
  className?: string
} & VariantProps<typeof noteStyles> &
  VariantProps<typeof noteLayoutStyles>

// =============================================================================
// Component
// =============================================================================
export function NoteRoot({ children, className, variant, layout }: NoteProps) {
  const resolvedVariant = variant ?? "info"
  const resolvedLayout = layout ?? "horizontal"
  const headingId = React.useId()
  const noteRef = React.useRef<HTMLElement>(null)
  const [hasHeading, setHasHeading] = React.useState(false)
  const [isDismissed, setIsDismissed] = React.useState(false)

  const registerHeading = React.useCallback(() => {
    setHasHeading(true)
    return () => setHasHeading(false)
  }, [])

  const dismiss = React.useCallback((focusTarget?: FocusTarget) => {
    if (noteRef.current) restoreFocusBeforeUnmount(noteRef.current, focusTarget)
    setIsDismissed(true)
  }, [])

  if (isDismissed) return null

  return (
    <NoteContext.Provider
      value={{
        variant: resolvedVariant,
        layout: resolvedLayout,
        headingId,
        registerHeading,
        dismiss
      }}
    >
      <section
        className={cn(noteStyles({ variant: resolvedVariant }), className)}
        aria-labelledby={hasHeading ? headingId : undefined}
        data-slot="note"
        ref={noteRef}
      >
        <div className={noteLayoutStyles({ layout })}>{children}</div>
      </section>
    </NoteContext.Provider>
  )
}

// =============================================================================
// Compound component
// =============================================================================
interface NoteComponent {
  (props: NoteProps): React.JSX.Element | null
  Icon: typeof NoteIcon
  ContentLeading: typeof NoteContentLeading
  Heading: typeof NoteHeading
  Content: typeof NoteContent
  Dismiss: typeof NoteDismiss
  Actions: typeof NoteActions
}

export const Note: NoteComponent = Object.assign(NoteRoot, {
  Icon: NoteIcon,
  ContentLeading: NoteContentLeading,
  Heading: NoteHeading,
  Content: NoteContent,
  Dismiss: NoteDismiss,
  Actions: NoteActions
} as const)
